import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import LoadingState from './common/LoadingState';
import ErrorStateView from './common/ErrorState';

/**
 * SKIN-432 — one row per ICD-11 root, with the three diagnosis fields as
 * COLUMNS in their own units. `lesions` counts LESIONS (the per-lesion
 * `lesion_diagnoses`); `clinicalVisits` and `finalVisits` count VISITS
 * (`derm_diagnosis` / `final_derm_dx`, which belong to the visit's D1 workup
 * and repeat independently of the lesions). A selector used to switch between
 * them, which forced one unit on all three and reported visits as lesions.
 *
 * SKIN-444 — the diagnostic workup is now recorded per lesion. `finalLesions`
 * counts lesions by their own final diagnosis; `finalVisits` counts a visit
 * whose final diagnosis is in the legacy visit-level D1 or on any lesion.
 * `clinicalVisits` only grows from visits recorded before the move.
 */
export interface DiseaseBreakdownRow {
    icd11Root: string;
    diseaseLabel: string;
    patients: number;
    visits: number;
    lesions: number;
    images: number;
    clinicalVisits: number;
    finalVisits: number;
    finalLesions: number;
}

export interface DiseaseBreakdownCoverage {
    filledLesions: number;
    totalLesions: number;
    filledPatients: number;
    totalPatients: number;
    clinicalVisits: number;
    finalVisits: number;
    finalLesions: number;
    closedVisits: number;
}

export interface DiseaseBreakdownByIcd11RootPureProps {
    loading: boolean;
    error?: Error | null;
    rows: DiseaseBreakdownRow[];
    coverage: DiseaseBreakdownCoverage | null;
    onRetry?: () => void;
}

const coveragePercent = (filled: number, total: number): number =>
    total > 0 ? Math.round((filled / total) * 100) : 0;

export const DiseaseBreakdownByIcd11RootPure: React.FC<DiseaseBreakdownByIcd11RootPureProps> = ({
    loading,
    error,
    rows,
    coverage,
    onRetry,
}) => {
    const headCells = useMemo(() => ([
        { id: 'disease', alignment: 'left', label: <Translate id="hospital.analytics.clinical.disease-breakdown.disease" /> },
        { id: 'patients', alignment: 'right', label: <Translate id="hospital.analytics.clinical.disease-breakdown.patients" /> },
        { id: 'visits', alignment: 'right', label: <Translate id="hospital.analytics.clinical.disease-breakdown.visits" /> },
        { id: 'lesions', alignment: 'right', label: <Translate id="hospital.analytics.clinical.disease-breakdown.lesions" /> },
        { id: 'clinicalVisits', alignment: 'right', label: <Translate id="hospital.analytics.clinical.disease-breakdown.clinical-visits" /> },
        { id: 'finalVisits', alignment: 'right', label: <Translate id="hospital.analytics.clinical.disease-breakdown.final-visits" /> },
        { id: 'finalLesions', alignment: 'right', label: <Translate id="hospital.analytics.clinical.disease-breakdown.final-lesions" /> },
        { id: 'images', alignment: 'right', label: <Translate id="hospital.analytics.clinical.disease-breakdown.images" /> },
    ]), []);

    const tableRows = useMemo(() => rows.map(r => ({
        id: r.icd11Root,
        disease: `${r.diseaseLabel} (${r.icd11Root})`,
        patients: r.patients,
        visits: r.visits,
        lesions: r.lesions,
        clinicalVisits: r.clinicalVisits,
        finalVisits: r.finalVisits,
        finalLesions: r.finalLesions,
        images: r.images,
    })), [rows]);

    // One line per column, each in its own unit — the lesion coverage says
    // whether lesions are being diagnosed at all, the visit lines say how much
    // of the workup is filled in, which nothing measured before.
    const coverageLine = coverage && (
        <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
                <Translate
                    id="hospital.analytics.clinical.disease-breakdown.coverage"
                    data={{
                        percent: coveragePercent(coverage.filledLesions, coverage.totalLesions),
                        filled: coverage.filledLesions,
                        total: coverage.totalLesions,
                    }}
                />
            </Typography>
            <Typography variant="body2" color="textSecondary">
                <Translate
                    id="hospital.analytics.clinical.disease-breakdown.final-lesion-coverage"
                    data={{
                        percent: coveragePercent(coverage.finalLesions, coverage.totalLesions),
                        filled: coverage.finalLesions,
                        total: coverage.totalLesions,
                    }}
                />
            </Typography>
            <Typography variant="body2" color="textSecondary">
                <Translate
                    id="hospital.analytics.clinical.disease-breakdown.visit-coverage"
                    data={{
                        clinicalPercent: coveragePercent(coverage.clinicalVisits, coverage.closedVisits),
                        clinical: coverage.clinicalVisits,
                        finalPercent: coveragePercent(coverage.finalVisits, coverage.closedVisits),
                        final: coverage.finalVisits,
                        closed: coverage.closedVisits,
                    }}
                />
            </Typography>
        </Box>
    );

    if (loading) {
        return (
            <LoadingState />
        );
    }
    if (error) return <ErrorStateView error={error} onRetry={onRetry} />;

    if (!rows.length) {
        const zeroCoverage = coverage && coverage.totalLesions > 0 && coverage.filledLesions === 0;
        return (
            <>
                {coverageLine}
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="textSecondary">
                        <Translate id={zeroCoverage
                            ? 'hospital.analytics.clinical.disease-breakdown.empty-zero-coverage'
                            : 'hospital.analytics.clinical.disease-breakdown.empty'
                        } />
                    </Typography>
                </Box>
            </>
        );
    }

    return (
        <>
            {coverageLine}
            <EnhancedTable noHeader noSelectable headCells={headCells} rows={tableRows} />
        </>
    );
};

const DiseaseBreakdownByIcd11Root = DiseaseBreakdownByIcd11RootPure;
export default DiseaseBreakdownByIcd11Root;
